import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigService } from '../../../commons';
import { DataValue, GenericResponse, IPaginated, MasterQueryParams } from '../../../interfaces';
import {
  ContractDetail,
  DraftContractValue,
  IncidentCommentResponse,
  IncidentFileResponse,
  PostIncidentFileResponse,
  TracingResponse,
  DraftList,
  ResponseSipsManual,
  RequestInfoManualSips,
  DraftAndTracingRequest,
  PostCreditAnalysisInfoResponse,
  PostCreditAnalysisInfo,
  CreditAnalysisInfo,
  EditApproveCifExperianBody,
  TracingInProgressRefactorDetail,
  DctmList,
  TracingContractsV3,
  TracingContractsV3ChildrensResponse,
  TracingPriceLevelOption,
  TracingContractStatuOption,
  TracingDraft
} from '../models/tracing.model';
import { FilterCupsOption, SocialReason } from '../../incidents/models/incidents.model';
import { FilterCampaignOption, FilterCifOption, FilterCommercialOption, FilterContractsTypeOption, FilterMonopointContractOption, FilterMultipointContractOption } from '../../customers/models/customers.model';
import { PaginationTableRequest } from '@shared/table/table.interface';

@Injectable({
  providedIn: 'root'
})
export class TracingService {
  private draft = new BehaviorSubject<GenericResponse<IPaginated<DraftList[]>>>(null);
  private draftFilter = new BehaviorSubject<GenericResponse<IPaginated<DraftList[]>>>(null);
  private draftDetails = new BehaviorSubject<GenericResponse<IPaginated<DraftList[]>>>(null);
  private tracing = new BehaviorSubject<GenericResponse<TracingResponse>>(null);
  private tracingFilter = new BehaviorSubject<GenericResponse<[]>>(null);
  private contractDetail: ContractDetail;
  private offers = new BehaviorSubject<GenericResponse<TracingResponse>>(null);
  private contracts = new BehaviorSubject<GenericResponse<TracingResponse>>(null);

  constructor(private http: HttpClient, private config: ConfigService) { }

  public getDraftPT(params: DraftAndTracingRequest): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getDraft'), { params }).pipe(
      tap((res: GenericResponse<any>) => {
        this.draft.next(res);
      })
    );
  }

  public getDraftFilter(params: any): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getDraftFilter'), { params }).pipe(
      tap((res: GenericResponse<any>) => {
        this.draftFilter.next(res);
      })
    );
  }

  public getDraftDetail(params: any): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getDraftDetail'), { params }).pipe(
      tap((res: GenericResponse<any>) => {
        this.draftDetails.next(res);
      })
    );
  }

  public getTracing(params: DraftAndTracingRequest): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getTracing'), { params }).pipe(
      tap((res: GenericResponse<any>) => {
        this.tracing.next(res);
      })
    );
  }

  public getTracingFilter(params: any): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getTracingFilter'), { params }).pipe(
      tap((res: GenericResponse<any>) => {
        this.tracingFilter.next(res);
      })
    );
  }

  public getContractDetail(): ContractDetail {
    return this.contractDetail;
  }

  public setContractDetail(params: ContractDetail, isExternal?: boolean): void {
    this.contractDetail = params;
    if (!isExternal) {
      this.contractDetail.codigoContrato = params.codigoCRM;
      this.contractDetail.direccionInstalacion = this.parseAddress(params);
      this.contractDetail.fechaFinContrato = params.fechaFin;
      this.contractDetail.consumoAnual = params.consumoEstimado;
    }
  }

  private parseAddress(params: ContractDetail): string {
    const array = [
      params.nombreDireccion,
      params.numeroDireccion,
      params.portalDireccion,
      params.escaleraDireccion,
      params.pisoDireccion,
      params.puertaDireccion
    ];
    return params.nombreTipoViaDireccion + ' ' + array.filter(x => x).join(', ');
  }

  public getOffers(params: DraftAndTracingRequest): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('tracing.getOffers'), { params }).pipe(
      tap((res: GenericResponse<any>) => {
        this.offers.next(res);
      })
    );
  }

  public getContracts(params: DraftAndTracingRequest): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('tracing.getContracts'), { params }).pipe(
      tap((res: GenericResponse<any>) => {
        this.contracts.next(res);
      })
    );
  }

  public downloadOffer(CodigoCRMOferta: string[]): Observable<any> {
    return this.http.get(this.config.getUrl('grandesCuentas.offers.getFile'), {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
      params: { CodigoCRMOferta }
    });
  }

  public downloadContract(CodigoCRMContrato: string): Observable<any> {
    return this.http.get(this.config.getUrl('tracing.getFile'), {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
      params: { CodigoCRMContrato }
    });
  }

  public getDataIncidentFiles(codContrato: string): Observable<IncidentFileResponse> {
    return this.http.get<IncidentFileResponse>(this.config.getUrl('tracing.getDataIncidentFiles', { codContrato }));
  }

  public sendIncidentComment(textoComentario: string, codigoContrato: string): Observable<IncidentCommentResponse> {
    return this.http.post<IncidentCommentResponse>(
      this.config.getUrl('tracing.sendIncidentComment'), { textoComentario, codigoContrato }
    );
  }

  public getIncidentFile(CodigoFichero: string, CodigoContrato: string, ext: string): Observable<Blob> {
    return this.http.get(this.config.getUrl('tracing.getIncidentFile'), {
      params: { CodigoFichero, CodigoContrato },
      headers: { Accept: this.getAcceptHeader(ext) },
      responseType: 'blob'
    });
  }

  public sendIncidentFile(requestBody: FormData, codContrato: string): Observable<PostIncidentFileResponse> {
    return this.http.post<PostIncidentFileResponse>(this.config.getUrl('tracing.sendIncidentFiles', { codContrato }), requestBody);
  }

  private getAcceptHeader(ext: string): string {
    const acceptList = {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      txt: 'text/plain',
    };
    return acceptList[ext];
  }

  public getDraftContract(params: DraftAndTracingRequest): Observable<GenericResponse<IPaginated<DraftContractValue>>> {
    return this.http.get<GenericResponse<IPaginated<DraftContractValue>>>(this.config.getUrl('tracing.getDraftContract'), { params });
  }

  public putApproveCifExperian(body: EditApproveCifExperianBody): Observable<GenericResponse> {
    return this.http.put<GenericResponse>(this.config.getUrl('tracing.putApproveCifExperian'), body);
  }

  public getFiltersDraftContract(params: any): Observable<GenericResponse<IPaginated<DataValue>>> {
    return this.http.get<GenericResponse<IPaginated<DataValue>>>(this.config.getUrl('tracing.getFiltersDraftContract'), { params });
  }

  public getSipManual(params: DraftAndTracingRequest): Observable<GenericResponse<IPaginated<ResponseSipsManual>>> {
    return this.http.get<GenericResponse<IPaginated<ResponseSipsManual>>>(this.config.getUrl('tracing.getSipManual'), { params });
  }

  public sendInfoCupsToSips(bodyRequest: RequestInfoManualSips): Observable<GenericResponse> {
    return this.http.put<GenericResponse>(this.config.getUrl('contrato.putInfoManualSips'), bodyRequest);
  }

  public downloadExcel(url: string, params: DraftAndTracingRequest): Observable<any> {
    return this.http.get(this.config.getUrl(url), {
      params,
      headers: { Accept: 'application/xls' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public deleteCreditAnalysisInfo(): Observable<GenericResponse> {
    return this.http.delete<GenericResponse>(this.config.getUrl('tracing.deleteCreditAnalysisInfoByUser'));
  }

  public postCreditAnalysisInfo(params: PostCreditAnalysisInfo): Observable<GenericResponse<PostCreditAnalysisInfoResponse>> {
    return this.http.post<GenericResponse<PostCreditAnalysisInfoResponse>>(
      this.config.getUrl('tracing.postCreditAnalysisInfo'), params
    );
  }

  public getCreditAnalysisInfo(): Observable<GenericResponse<CreditAnalysisInfo[]>> {
    return this.http.get<GenericResponse<CreditAnalysisInfo[]>>(this.config.getUrl('tracing.getCreditAnalysisInfoUser'));
  }

  public downloadCreditAnalysisExcel(): Observable<any> {
    return this.http.get(this.config.getUrl('tracing.getCreditAnalysisExcel'), {
      headers: { Accept: 'application/xls' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public downloadAllCreditAnalysisExcel(): Observable<any> {
    return this.http.get(this.config.getUrl('tracing.getAllCreditAnalysisExcel'), {
      headers: { Accept: 'application/xls' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public getTracingRefactor(params: DraftAndTracingRequest): Observable<GenericResponse<IPaginated<TracingContractsV3>>> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getTracingRefactor'), { params });
  }

  public getTracingDetailRefactor(idContratoCRM: string): Observable<GenericResponse<TracingInProgressRefactorDetail>> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getTracingDetailRefactor', { idContratoCRM }));
  }

  public getTracingDetailDCTMRefactor(idContratoCRM: string): Observable<GenericResponse<DctmList[]>> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getTracingDetailDCTMRefactor', { idContratoCRM }));
  }

  public getTracingChildrenRefactor(contractNum: string): Observable<GenericResponse<TracingContractsV3ChildrensResponse>> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getTracingChildrenRefactor', { contractNum }));
  }

  public getPriceLevel(params: PaginationTableRequest): Observable<GenericResponse<IPaginated<TracingPriceLevelOption>>> {
    return this.http.get<GenericResponse<IPaginated<TracingPriceLevelOption>>>(this.config.getUrl('maestros.getPriceLevelTracing'), { params: params as any });
  }

  public getContractsStatus(): Observable<GenericResponse<IPaginated<TracingContractStatuOption>>> {
    return this.http.get<GenericResponse<IPaginated<TracingContractStatuOption>>>(this.config.getUrl('maestros.getContractsStatusV3'));
  }

  public getDraftsES(params: DraftAndTracingRequest): Observable<GenericResponse<IPaginated<TracingDraft>>> {
    return this.http.get<GenericResponse<IPaginated<TracingDraft>>>(this.config.getUrl('contrato.getDraftV3'), { params });
  }

  public getDraftsContractsTypeV3(): Observable<GenericResponse<IPaginated<FilterContractsTypeOption>>> {
    return this.http.get<GenericResponse<IPaginated<FilterContractsTypeOption>>>(this.config.getUrl('maestros.getDraftsContractsTypeV3'));
  }

  public getDraftsPriceLevel(params: PaginationTableRequest): Observable<GenericResponse<IPaginated<TracingPriceLevelOption>>> {
    return this.http.get<GenericResponse<IPaginated<TracingPriceLevelOption>>>(this.config.getUrl('maestros.getDraftsPriceLevelV3'), { params: params as any });
  }
}
